// Contract: docs/adr/0024-player-ai-widget-contracts-and-sql-semantic-actions.md (V12 amendment).
import type * as Y from "yjs";
import { widgetsMap } from "@playbookai/collab-client";

/**
 * Task 9 — find the canvas entry for a widget IDENTITY, and say whether it can
 * be shown.
 *
 * The canvas Y.Map is keyed by an opaque reference key (`placement:<id>` for a
 * persisted placement, a nanoid for one added in this session); the entity id a
 * Player AI proposal reports lives in the entry's `widgetId` field. So the
 * lookup is a scan over that field, never `get(id)` — and never a title, an
 * array position or a saved coordinate, all of which a rename, a reorder or a
 * drag would invalidate. A moved widget is found at its new rect because the
 * rect is read here and now.
 *
 * Visibility is the CALLER's rule, passed in, because the canvas already owns
 * one (`resolveWidgetVisibility`) and two copies of it would eventually
 * disagree — the failure mode being a viewport that pans to a widget nobody can
 * see. A widget in a phase this SOLVE has not reached is REFUSED with a
 * sentence rather than revealed: revealing it would mean advancing the
 * workflow, which navigation is never allowed to do.
 */
export type CanvasRevealResolution =
  | { readonly status: "revealed"; readonly canvasWidgetId: string }
  | { readonly status: "missing" }
  | { readonly status: "hidden"; readonly reason: string };

export function resolveCanvasReveal(
  doc: Y.Doc,
  widgetId: string,
  isVisible: (entry: Y.Map<unknown>) => boolean,
): CanvasRevealResolution {
  const found = findCanvasEntry(doc, widgetId);
  if (found === null) return { status: "missing" };
  const [canvasWidgetId, entry] = found;
  if (!isVisible(entry)) {
    return { status: "hidden", reason: hiddenReason(entry) };
  }
  return { status: "revealed", canvasWidgetId };
}

function findCanvasEntry(
  doc: Y.Doc,
  widgetId: string,
): readonly [string, Y.Map<unknown>] | null {
  for (const [key, entry] of widgetsMap(doc).entries()) {
    if (entry.get("widgetId") === widgetId) return [key, entry];
  }
  return null;
}

/**
 * Why it cannot be shown, in the reader's terms. Names the phase when there is
 * one — "it is somewhere you have not opened yet" is actionable, "it is hidden"
 * is not.
 */
function hiddenReason(entry: Y.Map<unknown>): string {
  const phase = entry.get("phase");
  return typeof phase === "string" && phase !== "" && phase !== "any"
    ? `That widget is in the ${phase} phase, which this SOLVE hasn't reached yet.`
    : "That widget isn't on the visible part of this canvas right now.";
}
